export interface ApiErrorDetail {
  field?: string;
  code?: string;
  /** Present on 402 feature/credit-gate responses — see api.client.ts. */
  upgradeUrl?: string;
}

export class ApiError extends Error {
  public readonly statusCode: number;
  public readonly details: ApiErrorDetail;

  constructor(message: string, statusCode: number, details: ApiErrorDetail = {}) {
    super(message);
    this.name = "ApiError";
    this.statusCode = statusCode;
    this.details = details;
  }

  get isNotFound(): boolean        { return this.statusCode === 404; }
  get isConflict(): boolean        { return this.statusCode === 409; }
  get isUnauthorized(): boolean    { return this.statusCode === 401; }
  get isForbidden(): boolean       { return this.statusCode === 403; }
  get isValidationError(): boolean { return this.statusCode === 422; }
  get isNetworkError(): boolean    { return this.statusCode === 0; }
  /** 402 — feature gate (`feature_not_enabled` / `trial_expired`) or the
   *  notification-credit gate (`insufficient_credits`). See `details.code`
   *  (== the backend's `error` string) to distinguish which. */
  get isPaymentRequired(): boolean { return this.statusCode === 402; }
}

/**
 * There is no Cognito session, so the request was never sent.
 *
 * Distinct from a 401 the backend returned: a caller that catches this knows
 * the browser had no identity to present, not that the server rejected one.
 * Carries statusCode 401 so every existing `isUnauthorized` and
 * `statusCode === 401` branch keeps working unchanged: the difference that
 * matters is the message, which names the ended session instead of leaking the
 * backend's "Missing authorization header".
 */
export class NotAuthenticatedError extends ApiError {
  constructor(message = "Your session has ended. Please sign in again.") {
    super(message, 401, { code: "not_authenticated" });
    this.name = "NotAuthenticatedError";
  }
}
